import { NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";

import { RouterModule } from "@angular/router";
import { AuthGuard } from "@guards/auth/auth.guard";
import { CustomersTableComponent } from "./customers-table/customers-table.component";
import { RoleGuard } from "@guards/role/role.guard";
import { UsersTableComponent } from "../users/users-table/users-table.component";
import { RfidCardsTableComponent } from "../rfid-cards/rfid-cards-table/rfid-cards-table.component";
import { GroupsComponent } from "@components/manage/groups/groups.component";
import { ChargingStationsTableComponent } from "../charging-stations/charging-stations-table/charging-stations-table.component";
import { ProfileComponent } from "@components/manage/profile/profile.component";
import { CustomerOverviewComponent } from "./customer-overview/customer-overview.component";
import { DetailsChargingStationDialogComponent } from "@components/dialog/details-charging-station-dialog/details-charging-station-dialog.component";
import { DeleteChargingStationDialogComponent } from "@components/dialog/delete-charging-station-dialog/delete-charging-station-dialog.component";
import { EditUserDialogComponent } from "@components/dialog/edit-user-dialog/edit-user-dialog.component";
import { CreateUserDialogComponent } from "../../dialog/create-user-dialog/create-user-dialog.component";
import { CreateRfidCardDialogComponent } from "@components/dialog/create-rfid-card-dialog/create-rfid-card-dialog.component";
import { CustomerHistoryComponent } from "./customer-history/customer-history.component";
import { CustomerPermissionsComponent } from "./customer-permissions/customer-permissions.component";
import { CreateChargingStationDialogComponent } from "@components/dialog/create-charging-station-dialog/create-charging-station-dialog.component";
import { ChargingStationsStatusHistoryTableComponent } from "../charging-stations/charging-stations-status-history-table/charging-stations-status-history-table.component";
import { TransactionsComponent } from "@components/evaluation/transactions/transactions.component";
import { EditGroupDialogComponent } from "@components/dialog/edit-group-dialog/edit-group-dialog.component";
import { DeleteGroupDialogComponent } from "@components/dialog/delete-group-dialog/delete-group-dialog.component";
import { CreateCustomerDialogComponent } from "../../dialog/create-customer-dialog/create-customer-dialog.component";
import { DetailsTransactionDialogComponent } from "@components/dialog/details-transaction-dialog/details-transaction-dialog.component";
import { EditRfidCardDialogComponent } from "@components/dialog/edit-rfid-card-dialog/edit-rfid-card-dialog.component";
import { ChargingStatisticsComponent } from "@components/evaluation/charging-statistics/charging-statistics.component";
import { DeliveryNotesComponent } from "@components/evaluation/delivery-notes/delivery-notes.component";
import { DeliveryNoteActionsDialogComponent } from "@components/dialog/delivery-note-actions-dialog/delivery-note-actions-dialog.component";
import { DeliveryNoteSettings } from "@models/deliveryNotes";
import { PaymentReceiptsComponent } from "../payment-receipts/payment-receipts.component";
import { PricingsComponent } from "../pricings/pricings.component";
import { InvoicesComponent } from "../invoices/invoices.component";
import { CreatePricingDialogComponent } from "@components/dialog/create-pricing-dialog/create-pricing-dialog.component";
import { EditPricingDialogComponent } from "@components/dialog/edit-pricing-dialog/edit-pricing-dialog.component";
import { CreateInvoiceDialogComponent } from "@components/dialog/create-invoice-dialog/create-invoice-dialog.component";
import { PrintInvoiceDialogComponent } from "@components/dialog/print-invoice-dialog/print-invoice-dialog.component";
import { EditInvoiceSettingsDialogComponent } from "@components/dialog/edit-invoice-settings-dialog/edit-invoice-settings-dialog.component";
import { UnassignedRfidCardsTableComponent } from "../rfid-cards/unassigned-rfid-cards-table/unassigned-rfid-cards-table.component";
import { PayoutStatementsDialogComponent } from "@components/dialog/payout-statements-dialog/payout-statements-dialog.component";
import { DashboardComponent } from "@components/dashboard/dashboard.component";
import { MassCreateChargingStationDialogComponent } from "../../dialog/mass-create-charging-station-dialog/mass-create-charging-station-dialog.component";

@NgModule({
  declarations: [],
  imports: [
    CommonModule,
    RouterModule.forChild([
      {
        path: "customers",
        component: CustomersTableComponent,
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: ["Operator"] },
      },
      {
        path: "create",
        component: CreateCustomerDialogComponent,
        data: { isOperatorView: true },
      },
      {
        path: "customers/:key",
        component: CustomerOverviewComponent,
        canActivate: [AuthGuard, RoleGuard],
        data: { roles: ["Operator"] },
        children: [
          {
            path: "",
            redirectTo: "dashboard",
            pathMatch: "full",
          },
          {
            path: "dashboard",
            component: DashboardComponent,
            data: { isOperatorView: true },
            children: [
              {
                path: "transactions",
                component: TransactionsComponent,
                data: { isOperatorView: true },
                children: [
                  {
                    path: ":key",
                    component: DetailsTransactionDialogComponent,
                  },
                ],
              },
            ],
          },
          {
            path: "users",
            component: UsersTableComponent,
            data: { isOperatorView: true },
            children: [
              {
                path: "edit/:key",
                component: EditUserDialogComponent,
              },
              {
                path: "create",
                component: CreateUserDialogComponent,
              },
            ],
          },
          {
            path: "rfid",
            component: RfidCardsTableComponent,
            data: { isOperatorView: true },
            children: [
              {
                path: "create",
                component: CreateRfidCardDialogComponent,
              },
              {
                path: "edit/:key",
                component: EditRfidCardDialogComponent,
              },
            ],
          },
          {
            path: "unassigned-rfid",
            component: UnassignedRfidCardsTableComponent,
            data: { isOperatorView: true },
            children: [
              {
                path: "create",
                component: CreateRfidCardDialogComponent,
              },
              {
                path: "edit/:key",
                component: EditRfidCardDialogComponent,
              },
            ],
          },
          {
            path: "pricings",
            component: PricingsComponent,
            data: {
              roles: ["Operator"],
              isOperatorView: true,
            },
            children: [
              {
                path: "create",
                component: CreatePricingDialogComponent,
              },
              {
                path: "edit/:key",
                component: EditPricingDialogComponent,
              },
            ],
          },
          {
            path: "invoices",
            component: InvoicesComponent,
            data: {
              isOperatorView: true,
              roles: ["Operator"],
            },
            children: [
              {
                path: "create/:key",
                component: CreateInvoiceDialogComponent,
                data: { roles: ["Operator"] },
              },
              {
                path: "print/:key",
                component: PrintInvoiceDialogComponent,
                data: { roles: ["Operator"] },
              },
              {
                path: "settings",
                component: EditInvoiceSettingsDialogComponent,
                data: { roles: ["Operator"] },
              },
              {
                path: "payout-statements",
                component: PayoutStatementsDialogComponent,
                data: { roles: ["Operator"] },
              },
            ],
          },
          {
            path: "groups",
            component: GroupsComponent,
            data: { isOperatorView: true },
            children: [
              {
                path: "edit/:key",
                component: EditGroupDialogComponent,
              },
              {
                path: "delete/:key",
                component: DeleteGroupDialogComponent,
              },
            ],
          },
          {
            path: "chargingstations-status-log",
            component: ChargingStationsStatusHistoryTableComponent,
            children: [
              {
                path: "edit/:key",
                component: DetailsChargingStationDialogComponent,
              },
            ],
          },
          {
            path: "transactions",
            component: TransactionsComponent,
            data: { isOperatorView: true },
            children: [
              {
                path: ":key",
                component: DetailsTransactionDialogComponent,
              },
            ],
          },
          {
            path: "chargingstations",
            component: ChargingStationsTableComponent,
            data: { isOperatorView: true },
            children: [
              {
                path: "create",
                component: CreateChargingStationDialogComponent,
              },
              {
                path: "mass-create",
                component: MassCreateChargingStationDialogComponent,
                data: { roles: ["Operator"] },
              },
              {
                path: "edit/:key",
                component: DetailsChargingStationDialogComponent,
              },
              {
                path: "delete/:key",
                component: DeleteChargingStationDialogComponent,
              },
            ],
          },
          {
            path: "charging-statistics",
            component: ChargingStatisticsComponent,
            data: { isOperatorView: true },
          },
          {
            path: "delivery-notes",
            component: DeliveryNotesComponent,
            data: { isOperatorView: true },
            children: [
              {
                path: "edit/:key",
                component: DeliveryNoteActionsDialogComponent,
                data: { isOperatorView: true },
              },
              {
                path: "settings",
                component: DeliveryNoteSettings,
                data: { isOperatorView: true },
              },
            ],
          },
          {
            path: "profile",
            component: ProfileComponent,
            data: { isOperatorView: true },
          },
          {
            path: "permissions",
            component: CustomerPermissionsComponent,
          },
          {
            path: "history",
            component: CustomerHistoryComponent,
          },
          {
            path: "payment-receipts",
            component: PaymentReceiptsComponent,
            data: { isOperatorView: true },
          },
        ],
      },
    ]),
  ],
  providers: [],
})
export class CustomersModule {}
